import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from '../ui/alert-dialog';
import { Trophy, Trash, UploadSimple, Eye, EyeSlash, Image as ImageIcon } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';

const SLOTS = [1, 2, 3, 4, 5, 6, 7, 8];

export function FooterPartnersManagement() {
  const { getAuthHeader } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlot, setBusySlot] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/footer-partners`, { headers: getAuthHeader() });
      setPartners(r.data || []);
    } catch {
      toast.error('Failed to load partners');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const slotMap = SLOTS.reduce((m, s) => {
    m[s] = partners.find(p => Number(p.order) === s) || null;
    return m;
  }, {});

  const uploadImage = async (file) => {
    const form = new FormData();
    form.append('file', file);
    const r = await axios.post(`${API}/admin/upload-image`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return r.data.image_url; // already a /api/uploaded-images/{id} path
  };

  const saveSlot = async (slot, payload) => {
    const existing = slotMap[slot];
    if (existing) {
      await axios.put(`${API}/admin/footer-partners/${existing.id}`, payload, { headers: getAuthHeader() });
    } else {
      await axios.post(`${API}/admin/footer-partners`, { ...payload, order: slot }, { headers: getAuthHeader() });
    }
  };

  const handleFile = async (slot, file) => {
    setBusySlot(slot);
    try {
      const image_url = await uploadImage(file);
      const existing = slotMap[slot];
      await saveSlot(slot, {
        name: existing?.name || `Partner ${slot}`,
        image_url,
        link_url: existing?.link_url || '',
        order: slot,
        is_active: true,
      });
      toast.success(`Slot ${slot} updated`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
    setBusySlot(null);
  };

  const updateField = async (slot, field, value) => {
    const existing = slotMap[slot];
    if (!existing) return;
    setBusySlot(slot);
    try {
      await axios.put(`${API}/admin/footer-partners/${existing.id}`, { [field]: value }, { headers: getAuthHeader() });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
    setBusySlot(null);
  };

  const removeSlot = async (slot) => {
    const existing = slotMap[slot];
    if (!existing) return;
    setBusySlot(slot);
    try {
      await axios.delete(`${API}/admin/footer-partners/${existing.id}`, { headers: getAuthHeader() });
      toast.success(`Slot ${slot} cleared`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
    setBusySlot(null);
  };

  if (loading) return <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-lime-600 border-t-transparent mx-auto"></div></div>;

  return (
    <div data-testid="footer-partners-panel">
      <div className="mb-6">
        <h1 className="font-outfit font-bold text-2xl text-slate-900 flex items-center gap-2">
          <Trophy size={26} weight="duotone" className="text-lime-600" />
          Footer Partners & Awards
        </h1>
        <p className="text-slate-500 text-sm">
          Eight logo slots displayed in the footer&apos;s &quot;Get In Touch&quot; section (4 logos per row × 2 rows). Upload partner / award logos and optionally link each one to a destination URL. Toggle the eye to hide a slot from the public site without deleting it.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SLOTS.map(slot => {
          const p = slotMap[slot];
          const busy = busySlot === slot;
          const resolvedSrc = p?.image_url
            ? (p.image_url.startsWith('/api') ? `${BACKEND_URL}${p.image_url}` : p.image_url)
            : null;

          return (
            <div
              key={slot}
              className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col gap-3"
              data-testid={`partner-slot-${slot}`}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-slate-900 text-sm">Slot {slot}</h3>
                <div className="flex items-center gap-1">
                  {p && (
                    <button
                      type="button"
                      onClick={() => updateField(slot, 'is_active', !p.is_active)}
                      disabled={busy}
                      className={`p-1.5 rounded-lg border transition-colors ${p.is_active ? 'border-lime-200 text-lime-700 hover:bg-lime-50' : 'border-slate-200 text-slate-400 hover:bg-slate-50'}`}
                      title={p.is_active ? 'Visible — click to hide' : 'Hidden — click to show'}
                      data-testid={`partner-toggle-active-${slot}`}
                    >
                      {p.is_active ? <Eye size={14} /> : <EyeSlash size={14} />}
                    </button>
                  )}
                  {p && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          type="button"
                          className="p-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          disabled={busy}
                          data-testid={`partner-delete-${slot}`}
                        >
                          <Trash size={14} />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Clear slot {slot}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This removes &quot;{p.name}&quot; from the footer. You can re-upload anytime.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeSlot(slot)} className="bg-red-600 hover:bg-red-700">
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>

              {/* Image preview */}
              <div className="aspect-[3/2] w-full rounded-lg bg-slate-900 border border-slate-200 flex items-center justify-center overflow-hidden">
                {resolvedSrc ? (
                  <img
                    src={resolvedSrc}
                    alt={p.name}
                    className="max-w-[80%] max-h-[80%] object-contain"
                    data-testid={`partner-img-${slot}`}
                  />
                ) : (
                  <div className="text-slate-500 text-xs flex flex-col items-center gap-1">
                    <ImageIcon size={28} weight="duotone" />
                    <span>No logo uploaded</span>
                  </div>
                )}
              </div>

              {/* Upload */}
              <label className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 border-dashed text-sm cursor-pointer transition-colors ${busy ? 'border-slate-200 text-slate-400 cursor-not-allowed' : 'border-lime-300 text-lime-700 hover:bg-lime-50'}`}>
                <UploadSimple size={15} />
                {busy ? 'Working…' : (p ? 'Replace logo' : 'Upload logo')}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={busy}
                  onChange={e => e.target.files?.[0] && handleFile(slot, e.target.files[0])}
                  data-testid={`partner-file-${slot}`}
                />
              </label>

              {/* Name + link inputs (only show once an image exists) */}
              {p && (
                <div className="space-y-2">
                  <input
                    type="text"
                    defaultValue={p.name}
                    onBlur={e => e.target.value !== p.name && updateField(slot, 'name', e.target.value)}
                    placeholder="Partner name (for alt text)"
                    className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-lime-500"
                    data-testid={`partner-name-${slot}`}
                  />
                  <input
                    type="url"
                    defaultValue={p.link_url || ''}
                    onBlur={e => e.target.value !== (p.link_url || '') && updateField(slot, 'link_url', e.target.value)}
                    placeholder="Optional link URL (e.g. https://...)"
                    className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-lime-500"
                    data-testid={`partner-link-${slot}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600 space-y-1">
        <p><strong>Tip:</strong> Logos look best with a transparent background. PNG or SVG recommended.</p>
        <p>The dark preview here mirrors how the logo will appear in the footer.</p>
      </div>
    </div>
  );
}
